import React, { useRef, useState } from 'react';
import { Handle, Position } from '@xyflow/react';
import PropTypes from 'prop-types';
import {
  Box,
  Grow,
  IconButton,
  Paper,
  Typography,
  useTheme,
} from '@mui/material';
import { Close, SwapHoriz } from '@mui/icons-material';
import { useThemeContext } from '../../Context/ThemeContext';

const BacktrackNode = ({ id, data, deleteNode }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isHandleHover, setIsHandleHover] = useState(false);
  const hoverTimeout = useRef(null);

  const { darkMode } = useThemeContext();
  const muiTheme = useTheme();

  const handleMouseEnter = () => {
    hoverTimeout.current = setTimeout(() => setIsHovered(true), 400);
  };

  const handleMouseLeave = () => {
    clearTimeout(hoverTimeout.current);
    setIsHovered(false);
    setIsHandleHover(false);
  };

  const [mainLabel] = data.label.includes('(')
    ? data.label.split(' (')
    : [data.label, ''];

  const showOverlay = isHovered && !isHandleHover;

  return (
    <Box
      sx={{
        width: 70,
        height: 70,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Diamond/Rotated Square Shape for Distinct Visual */}
      <Box
        sx={{
          width: 45,
          height: 45,
          position: 'absolute',
          transform: 'rotate(45deg)',
          borderRadius: '8px',
          backgroundColor: '#9c27b6',
          boxShadow: isHovered
            ? '0 8px 24px rgba(156, 39, 176, 0.6)'
            : '0 4px 16px rgba(156, 39, 176, 0.5)',
          zIndex: isHovered ? 100 : 1,
          transition: 'all 0.25s ease',
          cursor: 'pointer',
          border: '3px solid #7b1fa2',
          '&:hover': {
            transform: 'rotate(45deg) translateY(-2px) scale(1.05)',
            boxShadow: '0 12px 28px rgba(156, 39, 176, 0.7)',
          },
        }}
      >
        {/* Inner content rotated back */}
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transform: 'rotate(-45deg)',
            position: 'relative',
          }}
        >
          {/* Reverse flow indicator icon */}
          <SwapHoriz
            sx={{
              fontSize: 25,
              color: '#fff',
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
            }}
          />
        </Box>
      </Box>

      {/* Outer glow ring */}
      <Box
        sx={{
          position: 'absolute',
          width: 70,
          height: 70,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(156, 39, 176, 0.2) 0%, transparent 70%)',
          zIndex: 0,
          pointerEvents: 'none',
          transition: 'opacity 0.25s ease',
          opacity: isHovered ? 1 : 0.6,
        }}
      />

      <Grow
        in={showOverlay}
        timeout={{ enter: 300, exit: 250 }}
        style={{ transitionDelay: showOverlay ? '100ms' : '0ms' }}
      >
        <Paper
          elevation={4}
          sx={{
            position: 'absolute',
            top: -8,
            left: -12,
            minWidth: 180,
            minHeight: 55,
            px: 2,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            borderRadius: 1,
            bgcolor: darkMode ? muiTheme.palette.background.paper : '#fff',
            border: '1px solid #9c27b6',
            zIndex: 200,
            transition: 'opacity 0.15s',
            pointerEvents: 'auto',
            boxShadow: '0 6px 16px rgba(156, 39, 176, 0.4)',
            '&:hover': { boxShadow: '0 8px 20px rgba(156, 39, 176, 0.5)' },
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              bgcolor: '#9c27b6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 2,
              boxShadow: '0 2px 8px rgba(156, 39, 176, 0.4)',
            }}
          >
            <SwapHoriz
              sx={{
                fontSize: 20,
                color: '#fff',
              }}
            />
          </Box>
          <Box sx={{ ml: 0.5, flex: 1 }}>
            <Typography
              sx={{
                color: '#9c27b6',
                fontSize: '0.7rem',
                marginBottom: '0.2rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Backtrack Node
            </Typography>
            <Typography
              sx={{
                color: darkMode ? '#fff' : '#212121',
                fontSize: '0.875rem',
                fontWeight: 600,
              }}
            >
              {mainLabel}
            </Typography>
          </Box>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              deleteNode(id, data.label);
            }}
            size="small"
            sx={{
              position: 'absolute',
              top: 6,
              right: 6,
              background: darkMode ? '#353535' : '#fff',
              color: muiTheme.palette.error.main,
              width: '1.25rem',
              height: '1.25rem',
              borderRadius: '50%',
              boxShadow: 1,
              zIndex: 2,
              '&:hover': {
                background: darkMode
                  ? muiTheme.palette.error.dark
                  : muiTheme.palette.error.dark,
                color: '#fff',
              },
            }}
          >
            <Close sx={{ fontSize: '0.75rem' }} />
          </IconButton>
        </Paper>
      </Grow>

      {(!isHovered || isHandleHover) && (
        <Handle
          type="target"
          position={Position.Right}
          onMouseEnter={() => setIsHandleHover(true)}
          onMouseLeave={() => setIsHandleHover(false)}
          style={{ 
            zIndex: 300,
            width: 8,
            height: 8,
            background: '#9c27b6',
            border: '2px solid #fff',
            boxShadow: '0 2px 4px rgba(156, 39, 176, 0.5)',
          }}
        />
      )}
      {(!isHovered || isHandleHover) && (
        <Handle
          type="source"
          position={Position.Left}
          onMouseEnter={() => setIsHandleHover(true)}
          onMouseLeave={() => setIsHandleHover(false)}
          style={{ 
            zIndex: 300,
            width: 8,
            height: 8,
            background: '#9c27b6',
            border: '2px solid #fff',
            boxShadow: '0 2px 4px rgba(156, 39, 176, 0.5)',
          }}
        />
      )}
    </Box>
  );
};

BacktrackNode.propTypes = {
  id: PropTypes.string.isRequired,
  data: PropTypes.shape({
    label: PropTypes.string.isRequired,
  }).isRequired,
  deleteNode: PropTypes.func.isRequired,
};

export default BacktrackNode;
